/**
 * Premium signup modal Alpine.js component
 * 
 * Handles user registration and login for premium subscriptions.
 * Features email checking, auto-switching between signup/login tabs,
 * Stripe checkout integration, and reCAPTCHA validation.
 */
window.premiumSignupModal = function () {
    return {
        loading: false,
        checkingEmail: false,
        currentStep: 1,
        showStepIndicator: false,
        showLoadingState: false,
        modalTitle: 'Bli Premium-medlem för att ladda ner dokument',
        activeTab: 'signup',

        signupForm: {
            email: '',
            password: '',
            plan: 'monthly'
        },
        loginForm: {
            email: '',
            password: '',
            plan: 'monthly'
        },
        errors: {},

        message: {
            text: '',
            type: 'info'
        },

        init() {
            console.log('Premium signup modal initialized with Alpine.js');
            console.log('Initial activeTab:', this.activeTab);
            this.loadRecaptcha();
        },

        async checkEmailExists(email) {
            try {
                const response = await this.apiCall('/api/check-email', {email});

                if (response.exists) {
                    if (response.has_premium) {
                        this.activeTab = 'login';
                        this.loginForm.email = email;

                        this.showMessage('success', 'Du har redan Premium! Logga in för att ladda ner dokumentet.');

                        this.$nextTick(() => {
                            this.$refs.loginPassword?.focus();
                        });
                    } else {
                        this.activeTab = 'login';
                        this.loginForm.email = email;

                        this.showMessage('info', 'Email finns redan. Växlade till inloggning.');

                        this.$nextTick(() => {
                            this.$refs.loginPassword?.focus();
                        });
                    }
                }
            } catch (error) {
                console.error('Error checking email:', error);
            }
        },

        async handleSignup() {
            if (this.loading) return;

            this.loading = true;
            this.errors = {};

            try {
                const recaptchaToken = await this.getRecaptchaToken('quick_register');

                // Clean URL: use URL constructor to get URL without hash or query params
                const url = new URL(window.location.href);
                const cleanUrl = url.origin + url.pathname;
                
                const response = await this.apiCall('/api/quick-register', {
                    ...this.signupForm,
                    plan: this.selectedPlan || this.signupForm.plan,
                    'g-recaptcha-response': recaptchaToken,
                    return_url: cleanUrl
                });

                if (response.success && response.has_premium) {
                    this.showMessage('success', response.message || 'Du har redan Premium!');
                    setTimeout(() => {
                        window.location.href = response.redirect_url || window.location.href;
                    }, 1500);
                } else if (response.success && response.checkout_url) {
                    this.showPaymentLoadingState();
                    setTimeout(() => {
                        window.location.href = response.checkout_url;
                    }, 1500);
                } else if (response.suggest_login && response.existing_email) {
                    // Email already exists - switch to login tab
                    this.showMessage('info', 'Denna e-postadress är redan registrerad. Växlar till inloggning...');

                    setTimeout(() => {
                        this.activeTab = 'login';
                        this.loginForm.email = response.existing_email;

                        this.$nextTick(() => {
                            this.$refs.loginPassword?.focus();
                        });
                    }, 1000);
                } else {
                    this.errors = response.errors || {};
                }
            } catch (error) {
                console.error('Signup error:', error);
                this.showMessage('danger', 'Ett fel uppstod. Försök igen.');
            } finally {
                this.loading = false;
            }
        },

        async handleLogin() {
            if (this.loading) return;

            this.loading = true;
            this.errors = {};

            try {
                // Clean URL: use URL constructor to get URL without hash or query params
                const url = new URL(window.location.href);
                const cleanUrl = url.origin + url.pathname;

                const recaptchaToken = await this.getRecaptchaToken('quick_login');

                const response = await this.apiCall('/api/quick-login', {
                    ...this.loginForm,
                    plan: this.selectedPlan || this.loginForm.plan,
                    'g-recaptcha-response': recaptchaToken,
                    return_url: cleanUrl
                });


                if (response.success) {
                    if (response.has_premium) {
                        this.showMessage('success', 'Inloggning lyckades! Laddar om sidan...');

                        setTimeout(() => {
                            window.location.href = response.redirect_url || window.location.href;
                        }, 1000);
                    } else if (response.checkout_url) {
                        this.showPaymentLoadingState();
                        setTimeout(() => {
                            window.location.href = response.checkout_url;
                        }, 1500);
                    }
                } else {
                    if (response.errors) {
                        this.errors = response.errors;
                    } else {
                        this.showMessage('danger', response.message || 'Felaktiga inloggningsuppgifter');
                    }
                }
            } catch (error) {
                console.error('Login error:', error);
                this.showMessage('danger', 'Ett fel uppstod. Försök igen.');
            } finally {
                this.loading = false;
            }
        },

        showPaymentLoadingState() {
            this.showLoadingState = true;
            this.showStepIndicator = true;
            this.currentStep = 2;
            this.modalTitle = 'Omdirigerar till betalning...';
        },

        showMessage(type, text) {
            this.message.type = type;
            this.message.text = text;

            setTimeout(() => {
                this.clearMessage();
            }, 5000);
        },

        clearMessage() {
            this.message.text = '';
            this.message.type = 'info';
        },

        clearFormErrors() {
            this.errors = {};
            this.clearMessage();
        },

        setPlan(plan) {
            this.signupForm.plan = plan;
            this.loginForm.plan = plan;
            if (this.selectedPlan !== undefined) {
                this.selectedPlan = plan;
            }
        },

        switchToTab(tab) {
            this.activeTab = tab;
            this.clearFormErrors();

            if (tab === 'login') {
                this.$nextTick(() => {
                    this.$refs.loginEmail?.focus();
                });
            }
        },

        switchToLogin(email = '') {
            this.activeTab = 'login';
            if (email) {
                this.loginForm.email = email;
            }
            this.errors = {};
            this.clearMessage();
            this.$nextTick(() => {
                this.$refs.loginPassword?.focus();
            });
        },

        async apiCall(url, data) {
            const csrfToken = this.$el.closest('body').querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                || this.$el.closest('body').querySelector('[name="_token"]')?.value
                || '';

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                    'Accept': 'application/json'
                },
                credentials: 'same-origin',
                body: JSON.stringify(data)
            });

            const contentType = response.headers.get('content-type');

            if (!contentType || !contentType.includes('application/json')) {
                const textResponse = await response.text();
                console.error('Expected JSON but got:', textResponse.substring(0, 500));
                throw new Error(`Server returned non-JSON response: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();

            if (!response.ok && result) {
                return result;
            }

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return result;
        },


        getError(field) {
            return this.errors[field] ? this.errors[field][0] : '';
        },

        hasError(field) {
            return this.errors[field] && this.errors[field].length > 0;
        },

        loadRecaptcha() {
            const siteKey = this.getRecaptchaSiteKey();
            if (!siteKey || typeof grecaptcha !== 'undefined') return;

            const script = document.createElement('script');
            script.src = 'https://www.google.com/recaptcha/api.js?render=' + siteKey;
            document.head.appendChild(script);
        },

        getRecaptchaSiteKey() {
            const body = this.$el.closest('body');
            const existingButton = body.querySelector('.g-recaptcha[data-sitekey]');
            if (existingButton) {
                return existingButton.getAttribute('data-sitekey');
            }

            const metaTag = body.querySelector('meta[name="recaptcha-site-key"]');
            return metaTag ? metaTag.getAttribute('content') : '';
        },

        async getRecaptchaToken(action) {
            if (typeof grecaptcha === 'undefined') {
                console.warn('reCAPTCHA not loaded');
                return '';
            }

            try {
                return await grecaptcha.execute(this.getRecaptchaSiteKey(), {action});
            } catch (error) {
                console.error('reCAPTCHA token generation failed:', error);
                return '';
            }
        },

    }
}